import ApplicationController from '../../javascript/controllers/application_controller'
import tippy from 'tippy.js'
import { tooltipOptions } from '../../javascript/lib/tooltip_options'

/**
 * Truncate Controller
 *
 * Attaches a tooltip with the element's own text only while CSS cuts that text
 * off, and destroys it again once the text fits. The tooltip opens on hover
 * and when focus enters the element, so it also opens for a link inside it.
 * With `focusable`, a cut element also takes keyboard focus itself.
 *
 * The cut is measured on connect, when the box resizes, when the text changes
 * in place and once web fonts have loaded; the tooltip reads the text when it
 * opens.
 *
 * @extends ApplicationController
 */
export default class extends ApplicationController {
  static values = {
    lines: { type: Number, default: 1 },
    focusable: { type: Boolean, default: false },
  }

  connect() {
    this.resizeObserver = new ResizeObserver(() => this.sync())
    this.resizeObserver.observe(this.element)
    this.mutationObserver = new MutationObserver(() => this.sync())
    this.mutationObserver.observe(this.element, {
      characterData: true,
      childList: true,
      subtree: true,
    })
    document.fonts?.ready.then(() => this.sync())
    this.sync()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    this.mutationObserver?.disconnect()
    this.resizeObserver = this.mutationObserver = null
    this.element._tippy?.destroy()
    if (this.focusableValue) this.element.removeAttribute('tabindex')
  }

  sync() {
    if (!this.element.isConnected) return

    const overflowing = this.overflowing
    this.element.dataset.truncated = overflowing

    if (overflowing && !this.element._tippy) {
      tippy(
        this.element,
        tooltipOptions({
          trigger: 'mouseenter focusin',
          content: this.text,
          onShow: (instance) => instance.setContent(this.text),
        }),
      )
    } else if (!overflowing) {
      this.element._tippy?.destroy()
    }

    if (this.focusableValue) {
      if (overflowing) this.element.setAttribute('tabindex', '0')
      else this.element.removeAttribute('tabindex')
    }
  }

  get text() {
    return this.element.textContent.trim()
  }

  get overflowing() {
    const { scrollWidth, clientWidth, scrollHeight, clientHeight } = this.element
    return this.linesValue === 1 ? scrollWidth > clientWidth : scrollHeight > clientHeight
  }
}
